import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, FileText, Link as LinkIcon, Mail, Phone, MessageCircle, Wifi, User, Calendar, CreditCard } from "lucide-react";
import Sidebar from "./Sidebar";
import SEOHelmet from "../../../components/SEOHelmet";

const qrTypes = [
  { id: "text", name: "Text", description: "Encode plain text messages or notes.", icon: <FileText className="w-8 h-8 text-cyan-700" /> },
  { id: "url", name: "Website / URL", description: "Link to any website directly.", icon: <LinkIcon className="w-8 h-8 text-cyan-700" /> },
  { id: "email", name: "Email", description: "Pre-fill email with subject and body.", icon: <Mail className="w-8 h-8 text-cyan-700" /> },
  { id: "phone", name: "Phone Number", description: "Dial a number directly when scanned.", icon: <Phone className="w-8 h-8 text-cyan-700" /> },
  { id: "sms", name: "SMS", description: "Send a pre-filled SMS message.", icon: <MessageCircle className="w-8 h-8 text-cyan-700" /> },
  { id: "wifi", name: "Wi-Fi", description: "Share Wi-Fi credentials for easy connection.", icon: <Wifi className="w-8 h-8 text-cyan-700" /> },
  { id: "vcard", name: "Contact / vCard", description: "Save contact info (name, email, phone).", icon: <User className="w-8 h-8 text-cyan-700" /> },
  { id: "event", name: "Event / Calendar", description: "Add event to calendar with date/time/location.", icon: <Calendar className="w-8 h-8 text-cyan-700" /> },
  { id: "payment", name: "Payment", description: "Generate QR for payments like UPI or Bitcoin.", icon: <CreditCard className="w-8 h-8 text-cyan-700" /> },
];

export default function QRTypesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const handleSelectType = (typeId) => {
    navigate("/qrstyling", { state: { qrType: typeId } });
  };

  // Filter QR types based on search input
  const filteredTypes = qrTypes.filter(
    (type) =>
      type.name.toLowerCase().includes(search.toLowerCase()) ||
      type.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 md:ml-64">
       <SEOHelmet
  title="QR Code Types"
  description="Explore different QR code types including URL, text, WiFi, vCard, and more. Learn how to use each QR code type and generate them instantly."
  keywords="QR code types, types of QR codes, URL QR code, WiFi QR code, vCard QR code, text QR code"
  url="https://omkarmore.com/qrtypes"
  image="https://omkarmore.com/LOGO_512.png"
/>


        {/* Back Button */}
        <div className="sticky top-0 z-30 bg-gray-50 py-4 px-6 shadow-sm">
          <button
            onClick={() => navigate("/tools")}
            className="flex items-center gap-2 text-cyan-700 font-medium hover:text-cyan-600 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Tools
          </button>
        </div>

        {/* Page Card */}
        <main className="bg-white p-6 md:p-8 rounded-2xl shadow-lg max-w-5xl mx-4 md:ml-8 mt-24 md:mt-12 w-full">
          <header className="text-center md:text-left mb-6">
            <h1 className="text-3xl md:text-4xl font-extrabold text-cyan-700">
              QR Code Types
            </h1>
            <p className="mt-2 text-gray-600 text-base md:text-lg">
              Select the type of QR code you want to generate. You can customize it later with colors, logos, gradients, and more.
            </p>
          </header>

          {/* Search Bar */}
          <div className="mb-6">
            <input
              type="text"
              placeholder="Search QR types..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base shadow-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition"
            />
          </div>

          {/* QR Type Grid */}
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTypes.length > 0 ? (
              filteredTypes.map((type) => (
                <div
                  key={type.id}
                  className="flex flex-col items-start bg-white p-5 rounded-xl shadow-md hover:shadow-xl transition cursor-pointer"
                  onClick={() => handleSelectType(type.id)}
                >
                  <div className="mb-3">{type.icon}</div>
                  <h2 className="text-lg font-semibold text-gray-800">{type.name}</h2>
                  <p className="text-gray-600 text-sm mt-1">{type.description}</p>
                </div>
              ))
            ) : (
              <p className="col-span-full text-gray-500 text-center">No QR types match your search.</p>
            )}
          </section>

          {/* Optional SEO Footer */}
          {/* <footer className="mt-12 text-gray-500 text-xs text-center">
            © {new Date().getFullYear()} FocusIn360. All rights reserved.
          </footer> */}
        </main>
        <br></br>
      </div>
    </div>
  );
}
